import React, { useState } from 'react';
import PasswordResetModal from './PasswordResetModal';
import { Box, Typography, Alert, Card, CardContent, Button, Divider } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';

/**
 * Complete Example Integration for Admin Password Reset
 * This component demonstrates how the PasswordResetModal is integrated
 * into an admin interface with proper state management and user feedback.
 */
const AdminPasswordResetExample = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [resetHistory, setResetHistory] = useState([]);

    const handleModalOpen = () => {
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const handlePasswordReset = (resetData) => {
        // This would typically be handled by the modal itself
        // But you can add additional logic here if needed
        console.log('Password reset completed:', resetData);

        // Add to reset history for demonstration
        setResetHistory(prev => [
            ...prev,
            {
                email: resetData.email,
                timestamp: new Date().toLocaleString(),
                status: 'Success'
            }
        ]);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Admin Password Reset System
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                This is a demonstration of the complete admin password reset functionality.
                The modal includes form validation, error handling, and success feedback.
            </Alert>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Password Reset Features
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>✅ Form validation - Requires all fields</li>
                        <li>✅ Loading states - Shows spinner during API calls</li>
                        <li>✅ Error handling - Displays API errors</li>
                        <li>✅ Success feedback - Shows confirmation message</li>
                        <li>✅ Auto-close - Closes modal after successful reset</li>
                        <li>✅ Responsive design - Works on all screen sizes</li>
                        <li>✅ Material-UI styling - Matches your existing design</li>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        API Endpoint Details
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <div><strong>URL:</strong> https://api.angelwatchedu.org/api/admin/admin-reset-password</div>
                        <div><strong>Method:</strong> POST</div>
                        <div><strong>Headers:</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            Content-Type: application/json<br />
                            Authorization: Bearer &lt;ADMIN_JWT_TOKEN&gt;
                        </div>
                        <div><strong>Body:</strong></div>
                        <div style={{ marginLeft: '20px' }}>
                            {JSON.stringify({
                                email: "user@example.com",
                                newPassword: "newSecurePassword123"
                            }, null, 2)}
                        </div>
                    </Typography>
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<LockIcon />}
                    onClick={handleModalOpen}
                    size="large"
                >
                    Reset User Password
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={() => setResetHistory([])}
                    size="large"
                >
                    Clear History
                </Button>
            </Box>

            {resetHistory.length > 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Recent Password Resets
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {resetHistory.map((reset, index) => (
                            <Box key={index} sx={{ mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2">
                                    <strong>Email:</strong> {reset.email}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Time:</strong> {reset.timestamp}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                    <strong>Status:</strong> {reset.status}
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Password Reset Modal */}
            <PasswordResetModal
                open={isModalOpen}
                onClose={handleModalClose}
            />
        </Box>
    );
};

export default AdminPasswordResetExample;
